/**
 * The reminder bars for attendance (13 Oct 2026; upgraded 14 Oct), on every
 * page but Attendance itself.
 *
 * An employee who has not marked in on a working day sees the bar with the
 * mark-in button in it — one tap after signing in. Once their shift has
 * ended and they are still marked in, the bar asks for the mark-out. The
 * owner and the attendance admin see how many people have nothing on today
 * once the office has opened. Gone when there is nothing to say. Someone who
 * both marks and manages may see two.
 *
 * Leave waiting for an answer moved to the approvals bar (17 Oct 2026), with
 * every other approval: one bar, one page.
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CalendarCheck, LogIn, LogOut, Users } from 'lucide-react';
import { clockMinutes, istClockOf } from '@bright/shared';

import { Button } from '@/components/ui';
import { formatNumber } from '@/lib/format';
import { useAttendanceSummary, useMark } from './attendance-api';

export function AttendanceNudge() {
  const location = useLocation();
  const summary = useAttendanceSummary().data;
  const markIn = useMark('in');
  const markOut = useMark('out');
  if (!summary || location.pathname === '/attendance') return null;

  const bars: ReactNode[] = [];
  const today = summary.today;
  if (summary.marks && today) {
    if (today.canMarkIn && !today.calendar) {
      bars.push(
        <div key="in" className="mb-3 flex items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-caption text-ink-2">
          <CalendarCheck className="h-4 w-4 shrink-0 text-accent" aria-hidden />
          <span className="min-w-0 flex-1">
            You have not marked in today.{' '}
            <Link to="/attendance" className="font-medium text-accent hover:underline">
              Your attendance
            </Link>
          </span>
          <Button size="sm" variant="primary" leftIcon={<LogIn className="h-4 w-4" aria-hidden />} loading={markIn.isPending} onClick={() => markIn.mutate()}>
            Mark in
          </Button>
        </div>,
      );
    } else if (today.canMarkOut && clockMinutes(istClockOf(new Date())) >= clockMinutes(today.closesAt)) {
      bars.push(
        <div key="out" className="mb-3 flex items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-caption text-ink-2">
          <CalendarCheck className="h-4 w-4 shrink-0 text-accent" aria-hidden />
          <span className="min-w-0 flex-1">Your shift ended at {today.closesAt} and you are still marked in.</span>
          <Button size="sm" variant="secondary" leftIcon={<LogOut className="h-4 w-4" aria-hidden />} loading={markOut.isPending} onClick={() => markOut.mutate()}>
            Mark out
          </Button>
        </div>,
      );
    }
  }

  if (summary.manages && summary.notMarked > 0) {
    bars.push(
      <Link key="manage" to="/attendance" className="mb-3 flex items-center gap-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-caption text-ink-2 hover:border-accent">
        <Users className="h-4 w-4 shrink-0 text-accent" aria-hidden />
        <span className="min-w-0 flex-1">
          {formatNumber(summary.notMarked)} {summary.notMarked === 1 ? 'person has' : 'people have'} nothing marked today.
        </span>
        <span className="shrink-0 font-medium text-accent">Open</span>
      </Link>,
    );
  }
  return bars.length > 0 ? <>{bars}</> : null;
}
